import { useEffect, useMemo, useRef, useState, type TouchEvent } from "react";
import {
  AlertCircle,
  CalendarX2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  MapPin,
  RefreshCw,
  ShieldAlert,
  UsersRound,
  X,
} from "lucide-react";
import type { MobileRole } from "../../lib/access";
import {
  formatDayNumber,
  formatLongDate,
  formatMonthShort,
  formatWeekRange,
  formatWeekdayInitial,
  groupEntriesByDate,
  isFull,
  statusLabel,
  statusTone,
  type ScheduleEntry,
} from "../../lib/schedule";
import { useClassSchedule } from "../../lib/useClassSchedule";

interface AgendaTabProps {
  readonly role: MobileRole;
  readonly token: string;
  readonly tenantId: string;
  readonly ctName: string;
}

const PULL_THRESHOLD = 56;

export function AgendaTab({ role, token, tenantId, ctName }: AgendaTabProps) {
  const schedule = useClassSchedule({ token, tenantId });
  const [selected, setSelected] = useState<ScheduleEntry | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [pull, setPull] = useState(0);
  const startY = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const autoSelected = useRef(true);
  const lastWeek = useRef<string | null>(null);
  const isMember = role === "member";
  const entriesByDate = useMemo(() => groupEntriesByDate(schedule.entries), [schedule.entries]);

  useEffect(() => {
    if (!schedule.weekStart || schedule.days.length === 0) return;
    const dates = schedule.days.map(day => day.date);
    const weekChanged = lastWeek.current !== schedule.weekStart;
    if (weekChanged) {
      lastWeek.current = schedule.weekStart;
      autoSelected.current = true;
    }

    if (!weekChanged && !autoSelected.current && selectedDate && dates.includes(selectedDate)) return;

    const todayInWeek = schedule.today && dates.includes(schedule.today) ? schedule.today : null;
    if (autoSelected.current && schedule.entries.length > 0) {
      if (todayInWeek && (entriesByDate.get(todayInWeek)?.length ?? 0) > 0) {
        if (selectedDate !== todayInWeek) setSelectedDate(todayInWeek);
        return;
      }
      const firstWithEntries = dates.find(date => (entriesByDate.get(date)?.length ?? 0) > 0);
      if (firstWithEntries) {
        if (selectedDate !== firstWithEntries) setSelectedDate(firstWithEntries);
        return;
      }
    }

    const fallback = todayInWeek ?? schedule.weekStart;
    if (selectedDate !== fallback) setSelectedDate(fallback);
  }, [schedule.weekStart, schedule.days, schedule.today, schedule.entries, entriesByDate, selectedDate]);

  useEffect(() => {
    setSelected(null);
  }, [schedule.weekStart, schedule.selectedCourtId]);

  const activeDate = selectedDate ?? schedule.weekStart;
  const dayEntries = activeDate ? entriesByDate.get(activeDate) ?? [] : [];

  const selectDay = (date: string) => {
    autoSelected.current = false;
    setSelectedDate(date);
  };

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    const scroller = rootRef.current?.closest(".shell__main") as HTMLElement | null;
    if ((scroller?.scrollTop ?? 0) > 0) return;
    startY.current = event.touches[0]?.clientY ?? null;
  };

  const onTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (startY.current === null) return;
    const currentY = event.touches[0]?.clientY ?? startY.current;
    const distance = currentY - startY.current;
    setPull(distance > 0 ? Math.min(80, distance * 0.55) : 0);
  };

  const onTouchEnd = () => {
    if (pull >= PULL_THRESHOLD && !schedule.weekLoading) schedule.refresh();
    startY.current = null;
    setPull(0);
  };

  return (
    <div
      className="tab-page agenda"
      ref={rootRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className={`agenda-pull ${pull > 0 ? "agenda-pull--visible" : ""}`.trim()} style={{ blockSize: pull }}>
        <RefreshCw size={16} className={schedule.weekLoading ? "agenda-spin" : ""} aria-hidden="true" />
        <span>{pull >= PULL_THRESHOLD ? "Solte para atualizar" : "Puxe para atualizar"}</span>
      </div>

      <header className="agenda-head">
        <div>
          <span className="agenda-head__eyebrow">{isMember ? "AULAS" : "AGENDA"}</span>
          <h1 className="agenda-head__title">{isMember ? "Grade" : "Agenda"}</h1>
          <p className="agenda-head__subtitle">{ctName}</p>
        </div>
        <button
          type="button"
          className="agenda-refresh"
          onClick={schedule.refresh}
          disabled={schedule.status === "loading" || schedule.weekLoading}
          aria-label="Atualizar agenda"
        >
          <RefreshCw size={18} className={schedule.weekLoading ? "agenda-spin" : ""} aria-hidden="true" />
        </button>
      </header>

      {schedule.weekStart ? (
        <section className="agenda-controls" aria-label="Filtros da agenda">
          <div className="week-nav">
            <button
              type="button"
              className="week-nav__button"
              onClick={schedule.goToPreviousWeek}
              aria-label="Semana anterior"
            >
              <ChevronLeft size={20} aria-hidden="true" />
            </button>
            <div className="week-nav__label">
              <strong>{formatWeekRange(schedule.weekStart)}</strong>
              <span>{schedule.isCurrentWeek ? "Semana atual" : "Semana selecionada"}</span>
            </div>
            <button
              type="button"
              className="week-nav__button"
              onClick={schedule.goToNextWeek}
              aria-label="Próxima semana"
            >
              <ChevronRight size={20} aria-hidden="true" />
            </button>
          </div>

          {!schedule.isCurrentWeek ? (
            <button type="button" className="agenda-today" onClick={schedule.goToCurrentWeek} aria-label="Voltar para hoje">
              Hoje
            </button>
          ) : null}

          <div className="agenda-day-strip">
            <div className="agenda-selected-day" aria-live="polite" aria-label={`Dia selecionado: ${formatLongDate(activeDate ?? schedule.weekStart)}`}>
              <strong>{formatDayNumber(activeDate ?? schedule.weekStart)}</strong>
              <span>{formatMonthShort(activeDate ?? schedule.weekStart)}</span>
            </div>
            <div className="agenda-week-days" role="group" aria-label="Escolher dia">
              {schedule.days.map(day => {
                const active = day.date === activeDate;
                return (
                  <button
                    key={day.date}
                    type="button"
                    className={`agenda-day-pill ${active ? "agenda-day-pill--selected" : ""} ${day.isToday ? "agenda-day-pill--today" : ""}`.trim()}
                    aria-pressed={active}
                    aria-label={formatLongDate(day.date)}
                    onClick={() => selectDay(day.date)}
                  >
                    <span className="agenda-day-pill__weekday">{formatWeekdayInitial(day.date)}</span>
                    <span className="agenda-day-pill__number">{day.dayNumber}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {schedule.courts.length > 0 ? (
            <div className="court-picker">
              <span className="court-picker__label">Quadra</span>
              <div className="court-picker__options" role="group" aria-label="Escolher quadra">
                {schedule.courts.map(court => {
                  const active = court.id === schedule.selectedCourtId;
                  return (
                    <button
                      key={court.id}
                      type="button"
                      className={`court-option ${active ? "court-option--active" : ""}`.trim()}
                      aria-pressed={active}
                      onClick={() => schedule.selectCourt(court.id)}
                    >
                      <MapPin size={14} aria-hidden="true" />
                      {court.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {schedule.status === "loading" ? <ScheduleSkeleton /> : null}

      {schedule.status === "denied" ? (
        <section className="agenda-state agenda-state--denied" role="status">
          <span className="agenda-state__icon"><ShieldAlert size={26} aria-hidden="true" /></span>
          <h2 className="agenda-state__title">Aulas indisponíveis</h2>
          <p className="agenda-state__text">{schedule.error}</p>
        </section>
      ) : null}

      {schedule.status === "error" ? (
        <section className="agenda-state" role="alert">
          <span className="agenda-state__icon"><AlertCircle size={26} aria-hidden="true" /></span>
          <h2 className="agenda-state__title">Não foi possível abrir a agenda</h2>
          <p className="agenda-state__text">{schedule.error}</p>
          <button type="button" className="agenda-state__action" onClick={schedule.retry}>
            Tentar novamente
          </button>
        </section>
      ) : null}

      {schedule.status === "ready" && schedule.weekLoading ? <ScheduleSkeleton compact /> : null}

      {schedule.status === "ready" && !schedule.weekLoading && schedule.courts.length === 0 ? (
        <section className="agenda-state agenda-state--empty" role="status">
          <span className="agenda-state__icon"><MapPin size={26} aria-hidden="true" /></span>
          <h2 className="agenda-state__title">Nenhuma quadra disponível</h2>
          <p className="agenda-state__text">O CT ainda não tem quadras ativas para montar a agenda.</p>
        </section>
      ) : null}

      {schedule.status === "ready" && !schedule.weekLoading && schedule.courts.length > 0 && activeDate ? (
        <>
          <div className="section__heading agenda-day-heading">
            <h2 className="section__title">{isMember ? "Aulas do dia" : "Sessões do dia"}</h2>
            <span className="section__hint">
              {dayEntries.length === 0
                ? "Sem aulas"
                : `${dayEntries.length} ${dayEntries.length === 1 ? "aula" : "aulas"}`}
            </span>
          </div>

          {dayEntries.length === 0 ? (
            <section className="agenda-state agenda-state--empty" role="status">
              <span className="agenda-state__icon"><CalendarX2 size={26} aria-hidden="true" /></span>
              <h2 className="agenda-state__title">Sem aulas neste dia</h2>
              <p className="agenda-state__text">Escolha outro dia ou outra quadra para ver a grade.</p>
            </section>
          ) : (
            <div className="agenda-class-list">
              {dayEntries.map(entry => (
                <button
                  key={`${entry.classId}-${entry.date}`}
                  type="button"
                  className={`agenda-class agenda-class--${statusTone(entry.status)}`}
                  onClick={() => setSelected(entry)}
                  aria-label={`${entry.name}, ${entry.startTime} às ${entry.endTime}, ${statusLabel(entry.status)}`}
                >
                  <span className="agenda-class__main">
                    <span className="agenda-class__title">{entry.name}</span>
                    <span className="agenda-class__coach">{entry.coachName || "Professor"}</span>
                  </span>
                  <span className="agenda-class__side">
                    <span className="agenda-class__time">{entry.startTime} – {entry.endTime}</span>
                    <span className="agenda-class__occupancy">
                      <UsersRound size={14} aria-hidden="true" />
                      {entry.enrolledCount}/{entry.capacity}
                    </span>
                    <span className={`agenda-status agenda-status--${statusTone(entry.status)}`}>
                      {statusLabel(entry.status)}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : null}

      {selected ? <ClassDetailsSheet entry={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

function ScheduleSkeleton({ compact = false }: { readonly compact?: boolean }) {
  return (
    <div className={`agenda-skeleton ${compact ? "agenda-skeleton--compact" : ""}`.trim()} aria-busy="true" aria-label="Carregando agenda">
      {Array.from({ length: compact ? 2 : 3 }).map((_, index) => (
        <div className="agenda-skeleton__card" key={index}>
          <span className="agenda-skeleton__body">
            <span className="agenda-skeleton__line agenda-skeleton__line--title" />
            <span className="agenda-skeleton__line agenda-skeleton__line--short" />
          </span>
          <span className="agenda-skeleton__side">
            <span className="agenda-skeleton__line" />
            <span className="agenda-skeleton__line agenda-skeleton__line--short" />
          </span>
        </div>
      ))}
    </div>
  );
}

interface ClassDetailsSheetProps {
  readonly entry: ScheduleEntry;
  readonly onClose: () => void;
}

function ClassDetailsSheet({ entry, onClose }: ClassDetailsSheetProps) {
  const tone = statusTone(entry.status);
  return (
    <div className="sheet-layer" role="dialog" aria-modal="true" aria-label={`Detalhes de ${entry.name}`}>
      <button type="button" className="sheet-backdrop" aria-label="Fechar" onClick={onClose} />
      <div className="sheet">
        <div className="sheet__handle" aria-hidden="true" />
        <div className="sheet__header">
          <div>
            <h2 className="sheet__title">{entry.name}</h2>
            <p className="sheet__subtitle">{formatLongDate(entry.date)}</p>
          </div>
          <button type="button" className="sheet__close" onClick={onClose} aria-label="Fechar detalhes">
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <span className={`agenda-status agenda-status--${tone}`}>{statusLabel(entry.status)}</span>

        <dl className="agenda-detail">
          <div>
            <dt>Horário</dt>
            <dd>{entry.startTime} – {entry.endTime}</dd>
          </div>
          <div>
            <dt>Modalidade</dt>
            <dd>{entry.sportName ?? "A aula"}</dd>
          </div>
          <div>
            <dt>Professor</dt>
            <dd>{entry.coachName || "Não informado"}</dd>
          </div>
          <div>
            <dt>Quadra</dt>
            <dd>{entry.courtName || "Não informada"}</dd>
          </div>
          <div>
            <dt>Ocupação</dt>
            <dd>
              {entry.enrolledCount}/{entry.capacity}
              {isFull(entry) ? " · Lotada" : ` · ${entry.availableSpots} ${entry.availableSpots === 1 ? "vaga" : "vagas"}`}
            </dd>
          </div>
        </dl>

        {entry.reason ? (
          <div className="agenda-detail__reason">
            <Clock3 size={16} aria-hidden="true" />
            <span>{entry.reason}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
